import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Field, FilePicker, InfoTip, inputCls } from '../components/shared/FormKit';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { fetchJSON } from '../utils/api';
import { formatDate } from '../utils/dateFormat';
import { hintOf } from '../utils/uploadKinds';

// Training absence declaration — submission form
// (docs/modules/workflow-ld-absence.md). Always for the logged-in user; the
// backend resolves the staff link from the login email (identity rule: email
// only). On approval the absence is recorded against the training session.
const WorkflowAbsenceNew = () => {
  const navigate = useNavigate();
  // The Overview's "file an absence" shortcut arrives as ?sessionId=… —
  // pre-pick that session once the options load (invalid ids simply no-op:
  // the <select> ignores a value that matches no option).
  const [searchParams] = useSearchParams();
  const presetSessionId = searchParams.get('sessionId') || '';

  const [sessions, setSessions] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [otherLabel, setOtherLabel] = useState('Others');
  const [optionsError, setOptionsError] = useState(null);
  const [sessionId, setSessionId] = useState('');
  const [reasonCategory, setReasonCategory] = useState('');
  const [reasonDetail, setReasonDetail] = useState('');
  const [remarks, setRemarks] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    // Wide window (not the 90-day default): the Overview keeps nagging about
    // an unfiled absence however old it is, so whatever it links to must be
    // pickable here.
    fetchJSON('/api/ld/absence-requests/options?days=3650')
      .then((d) => {
        if (!active) return;
        const list = d.sessions || [];
        setSessions(list);
        setReasons(d.reasons || []);
        setOtherLabel(d.other || 'Others');
        if (presetSessionId && list.some((s) => String(s.id) === presetSessionId)) {
          setSessionId(presetSessionId);
        }
      })
      .catch((e) => { if (active) setOptionsError(e.message); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedSession = sessions.find(s => String(s.id) === String(sessionId)) || null;
  // The managed list marks one option as the catch-all; picking it means the
  // reviewer needs the specifics in writing (same rule as the HR tab).
  const needsDetail = reasonCategory === otherLabel;

  // Everything still empty, keyed to the control it belongs to (the same
  // `field=` keys the detail page's return comments anchor to). Details is a
  // gap only while the catch-all reason is picked.
  const gaps = useMemo(() => {
    const g = [];
    if (!sessionId) g.push({ key: 'sessionTitle', label: 'Training Session' });
    if (!reasonCategory) g.push({ key: 'reasonCategory', label: 'Reason' });
    if (needsDetail && !reasonDetail.trim()) g.push({ key: 'reasonDetail', label: 'Details' });
    return g;
  }, [sessionId, reasonCategory, needsDetail, reasonDetail]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      form.set('sessionId', sessionId);
      form.set('reasonCategory', reasonCategory);
      if (reasonDetail.trim()) form.set('reasonDetail', reasonDetail.trim());
      if (remarks.trim()) form.set('remarks', remarks.trim());
      files.forEach(f => form.append('files', f));

      const data = await fetchJSON('/api/ld/absence-requests', { method: 'POST', body: form });
      navigate(`/workflow/absence/${data.request.id}`);
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/absence"
      backLabel="Training Absence"
      title="New Training Absence Declaration"
      subtitle="Tell HR why you missed a training session."
      error={error || validation.error}
      onSubmit={submit}
      submitLabel="Submit"
      submitting={saving}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Session">
          <Field
            label="Training Session"
            required
            field="sessionTitle"
            hint={<>Past sessions you were expected at and are not already recorded for. <InfoTip text="If you actually attended, file an attendance dispute instead." /></>}
          >
            <select
              value={sessionId}
              onChange={e => setSessionId(e.target.value)}
              className={inputCls}
            >
              <option value="">Select…</option>
              {sessions.map(s => (
                <option key={s.id} value={s.id}>
                  {s.courseTitle} — {formatDate(s.date)}
                </option>
              ))}
            </select>
            {/* Read-back of the picked session, so a mis-click is visible before
                submitting (workflow-ui.md §4.2: pick, then confirm). */}
            {selectedSession && (
              <p className="mt-1 text-xs text-slate-500">
                {[selectedSession.sessionCode, formatDate(selectedSession.date)].filter(Boolean).join(' · ')}
              </p>
            )}
            {optionsError && <p className="mt-1 text-xs text-red-600">{optionsError}</p>}
            {/* An empty list is now a statement about you, not about the firm's
                calendar, so it has to say what to do next — otherwise the one
                case that matters (expected at a session that is not listed)
                looks like the system is broken. */}
            {!optionsError && sessions.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">
                Nothing to declare — no past session is still open for you.
              </p>
            )}
          </Field>
        </FormSection>

        <FormSection step={2} title="Reason">
          <Field label="Reason" required field="reasonCategory">
            <select
              value={reasonCategory}
              onChange={e => setReasonCategory(e.target.value)}
              className={inputCls}
            >
              <option value="">Select…</option>
              {reasons.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field
            label="Details"
            required={needsDetail}
            field="reasonDetail"
            hint={needsDetail ? `Required when the reason is '${otherLabel}'.` : null}
          >
            <textarea rows="2" value={reasonDetail} onChange={e => setReasonDetail(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>

        {/* No "do you have evidence?" tick-box: attaching the
            file IS the answer, and asking twice lets the two disagree. */}
        <FormSection step={3} title="Attachments">
          <Field
            label="Supporting Evidence"
            hint={<>MC, leave approval, … {hintOf('docsAndImages', { multiple: true })} 5 files at a time. <InfoTip align="right" text="What you attach here is copied onto the training record when the declaration is approved." /></>}
          >
            <FilePicker
              multiple
              kind="docsAndImages"
              disabled={saving}
              onChange={e => setFiles(Array.from(e.target.files || []))}
              files={files}
            />
          </Field>
        </FormSection>

        <FormSection step={4} title="Remarks">
          <Field label="Remarks">
            <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowAbsenceNew;
